/*
  KinkKart homepage rack (rack-v1, 2026-10-16): the behaviour behind sections/kk-rack.liquid.

  One tee faces you (.is-front); the rest hang side-on, pushed apart around it
  (.is-before / .is-after, transforms only). What turns a tee round:
    - a mouse (hover mode: fine pointer, 990px+): pointing at it;
    - touch / small screens (swipe mode): it settling in the middle of the rail
      after a swipe, or a tap on it (the rail glides it to the middle);
    - the keyboard: tabbing to it.
  Clicking or tapping the tee that faces you (or "Pick your size") opens the
  close-up. Each tee is also a real link to its product page, for crawlers and for
  ctrl/cmd/middle-click, which are left alone.

  The close-up shows the tee big with every colour (its kk_rack.front photos) and
  adds sizes through the cart drawer's own code (kkReadVariants / kkPaint /
  kkVariantFor / kkAddVariant in assets/cart-drawer.js), so an add opens the drawer
  exactly as quick add does. Without that script it falls back to the product page.
*/
(() => {
  if (customElements.get('kk-rack')) return;

  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hoverMode = () => window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)').matches;
  const pad2 = (n) => String(n).padStart(2, '0');
  const cartReady = () =>
    typeof kkReadVariants === 'function' && typeof kkPaint === 'function' && typeof kkAddVariant === 'function';

  class KkRack extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.items = Array.from(this.querySelectorAll('[data-kk-item]'));
      if (!this.items.length) return;
      this.scroller = this.querySelector('[data-kk-scroller]');
      this.caption = this.querySelector('.kk-rack__caption');
      this.dialog = this.querySelector('[data-kk-dialog]');
      this.front = this.items.findIndex((item) => item.classList.contains('is-front'));
      if (this.front < 0) this.setFront(0, true);

      this.items.forEach((item, i) => {
        const tee = item.querySelector('[data-kk-tee]');
        tee.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse' && hoverMode()) this.hoverTo(i);
        });
        tee.addEventListener('pointerleave', () => clearTimeout(this.hoverTimer));
        // Keyboard only: a tap also focuses, and must not count as the turn.
        tee.addEventListener('focus', () => {
          if (tee.matches(':focus-visible')) {
            this.setFront(i);
            if (!hoverMode()) this.centre(i, true);
          }
        });
        tee.addEventListener('click', (event) => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.tap(i);
        });
      });
      this.querySelector('[data-kk-open]').addEventListener('click', () => this.open(this.front));

      this.scroller.addEventListener('scroll', () => this.onScroll(), { passive: true });
      if ('onscrollend' in window) this.scroller.addEventListener('scrollend', () => this.settle());
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (!hoverMode()) this.centre(this.front, false);
        }, 150);
      });
      // Swipe mode opens with the start tee in the middle of the rail.
      requestAnimationFrame(() => {
        if (!hoverMode()) this.centre(this.front, false);
      });

      this.setupDialog();
    }

    /* ---------- The rail ---------- */

    hoverTo(i) {
      clearTimeout(this.hoverTimer);
      // A short wait, so sweeping the pointer across the rail doesn't spin every tee.
      this.hoverTimer = setTimeout(() => this.setFront(i), 70);
    }

    setFront(i, quiet = false) {
      if (i === this.front || !this.items[i]) return;
      this.front = i;
      this.items.forEach((item, k) => {
        item.classList.toggle('is-front', k === i);
        item.classList.toggle('is-before', k < i);
        item.classList.toggle('is-after', k > i);
      });
      if (quiet) return;
      const item = this.items[i];
      if (!reduced()) {
        item.classList.remove('is-swinging');
        void item.offsetWidth; // restart the swing
        item.classList.add('is-swinging');
        clearTimeout(item.swingTimer);
        item.swingTimer = setTimeout(() => item.classList.remove('is-swinging'), 950);
      }
      this.paintCaption(item);
    }

    paintCaption(item) {
      const set = () => {
        const slogan = this.caption.querySelector('[data-kk-c-slogan]');
        slogan.textContent = item.dataset.slogan || '';
        slogan.hidden = !item.dataset.slogan;
        this.caption.querySelector('[data-kk-c-name]').textContent = item.dataset.name;
        this.caption.querySelector('[data-kk-c-price]').textContent = item.dataset.price;
        this.caption.classList.remove('is-changing');
      };
      clearTimeout(this.captionTimer);
      if (reduced()) return set();
      this.caption.classList.add('is-changing');
      this.captionTimer = setTimeout(set, 130);
    }

    tap(i) {
      if (hoverMode() || i === this.front) {
        this.open(i);
        return;
      }
      // Swipe mode, a tee at the side: glide it to the middle; it turns as it arrives.
      this.steering = i;
      this.setFront(i);
      this.centre(i, true);
    }

    centre(i, smooth) {
      const item = this.items[i];
      if (!item) return;
      const left = item.offsetLeft + item.offsetWidth / 2 - this.scroller.clientWidth / 2;
      this.scroller.scrollTo({ left, behavior: smooth && !reduced() ? 'smooth' : 'auto' });
    }

    onScroll() {
      if (hoverMode()) return;
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.settle(), 160);
    }

    // The tee nearest the middle of the rail faces you once a swipe comes to rest.
    settle() {
      if (hoverMode()) return;
      clearTimeout(this.scrollTimer);
      const middle = this.scroller.scrollLeft + this.scroller.clientWidth / 2;
      let nearest = 0;
      let best = Infinity;
      this.items.forEach((item, k) => {
        const gap = Math.abs(item.offsetLeft + item.offsetWidth / 2 - middle);
        if (gap < best) {
          best = gap;
          nearest = k;
        }
      });
      if (this.steering != null) {
        if (nearest !== this.steering) return; // still gliding to the tapped tee
        this.steering = null;
      }
      this.setFront(nearest);
    }

    /* ---------- The close-up ---------- */

    setupDialog() {
      const d = this.dialog;
      // Out of the section, so nothing on the page can clip it or stack over it.
      // Not in the theme editor, which re-renders the section in place.
      if (!(window.Shopify && Shopify.designMode)) document.body.appendChild(d);
      this.panel = d.querySelector('.kk-rack__panel');
      this.big = d.querySelector('[data-kk-big]');
      this.body = d.querySelector('[data-kk-d-body]');
      this.info = d.querySelector('.kk-rack__info');
      this.link = d.querySelector('[data-kk-d-link]');

      d.addEventListener('click', (event) => {
        if (event.target.closest('[data-kk-close]')) return this.close();
        if (event.target.closest('[data-kk-prev]')) return this.show(this.current - 1);
        if (event.target.closest('[data-kk-next]')) return this.show(this.current + 1);
        this.choose(event);
      });
      d.addEventListener('keydown', (event) => {
        if (event.target.closest('input, textarea')) return;
        if (event.key === 'ArrowLeft') this.show(this.current - 1);
        if (event.key === 'ArrowRight') this.show(this.current + 1);
      });
      document.addEventListener('keyup', (event) => {
        if (event.code === 'Escape' && this.isOpen()) this.close();
      });
    }

    isOpen() {
      return !this.dialog.hidden;
    }

    fill(i) {
      const item = this.items[i];
      const d = this.dialog;
      this.current = i;
      d.querySelector('[data-kk-num]').textContent = pad2(i + 1);
      const slogan = d.querySelector('[data-kk-d-slogan]');
      slogan.textContent = item.dataset.slogan || '';
      slogan.hidden = !item.dataset.slogan;
      d.querySelector('[data-kk-d-name]').textContent = item.dataset.name;
      d.querySelector('[data-kk-d-fit]').textContent = item.dataset.fit;
      d.querySelector('[data-kk-d-price]').textContent = item.dataset.price;
      this.link.href = item.dataset.url;

      this.body.replaceChildren(item.querySelector('[data-kk-detail]').content.cloneNode(true));
      this.kk = cartReady() ? kkReadVariants(this.body) : null;
      if (this.kk) {
        kkPaint(this.body, this.kk, item.dataset.yourSize);
        this.info.classList.remove('no-cart');
      } else {
        // No cart drawer on this page: the product page does the adding.
        const sizes = this.body.querySelector('[data-kk-sizes]');
        if (sizes) sizes.previousElementSibling.hidden = sizes.hidden = true;
        this.info.classList.add('no-cart');
      }
      const first = this.body.querySelector('[data-kk-fronts] li');
      this.showColour(this.kk ? this.kk.color : first && first.dataset.colour, false);
    }

    showColour(colour, follow = true) {
      const item = this.items[this.current];
      const photos = Array.from(this.body.querySelectorAll('[data-kk-fronts] li'));
      const photo = photos.find((li) => li.dataset.colour === colour) || photos[0];
      if (photo) {
        this.big.sizes = '(min-width: 750px) 520px, 90vw';
        this.big.srcset = photo.dataset.srcset;
        this.big.src = photo.dataset.src;
        this.big.alt = `${item.dataset.name} tee in ${photo.dataset.colour.toLowerCase()}`;
      }
      if (follow && this.kk && typeof kkShownVariant === 'function') {
        const variant = kkShownVariant(this.kk);
        const url = new URL(item.dataset.url, window.location.href);
        if (variant) url.searchParams.set('variant', variant.id);
        this.link.href = url.pathname + url.search;
      }
    }

    choose(event) {
      if (!this.kk) return;
      const item = this.items[this.current];
      const swatch = event.target.closest('[data-kk-color]');
      if (swatch && this.body.contains(swatch)) {
        if (swatch.disabled) return;
        this.kk.color = swatch.dataset.kkColor;
        kkPaint(this.body, this.kk, item.dataset.yourSize);
        this.showColour(this.kk.color);
        return;
      }
      const button = event.target.closest('[data-kk-size]');
      if (!button || !this.body.contains(button) || button.disabled || this.busy) return;
      const variant = kkVariantFor(this.kk, button.dataset.kkSize);
      if (!variant || !variant.available) return;

      this.busy = true;
      this.body.classList.add('is-busy');
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      const error = this.body.querySelector('[data-kk-error]');
      if (error) error.hidden = true;
      const done = () => {
        this.busy = false;
        this.body.classList.remove('is-busy');
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      };
      // 'kk-quick-add': the drawer skips its own re-render for this source, because
      // kkAddVariant has already rendered it.
      kkAddVariant(variant.id, 'kk-quick-add')
        .then(() => {
          done();
          // After the drawer's open timer, so the page lock passes straight to it.
          setTimeout(() => this.close(true));
        })
        .catch((e) => {
          done();
          if (error) {
            error.textContent = (e && e.message) || (window.cartStrings && window.cartStrings.error) || 'Something went wrong.';
            error.hidden = false;
          }
        });
    }

    show(i) {
      const n = this.items.length;
      const k = ((i % n) + n) % n;
      this.fill(k);
      // The rail behind follows, so closing lands on this tee.
      this.setFront(k);
      if (!hoverMode()) this.centre(k, false);
    }

    /*
      Opening and closing share one motion: a "ghost" copy of the tee photo, above
      everything, flies between the rail and the close-up. Opening, the box fades and
      grows in on the same 460 ms clock (CSS .is-open), so tee and box land together.
      Closing, the box goes at once (CSS .is-leaving, 90 ms) and the ghost flies the
      tee back onto its hanger while the scrim fades.
    */
    open(i) {
      if (this.isOpen() || this.closing || !this.items[i]) return;
      this.setFront(i);
      this.fill(i);
      this.opener = document.activeElement;
      const d = this.dialog;
      const railImg = this.items[i].querySelector('.kk-rack__img--front');
      const fly = !reduced() && this.visible(railImg) && railImg.complete && railImg.naturalWidth > 0;
      const from = fly && railImg.getBoundingClientRect();
      if (fly) {
        // The close-up starts on the photo the rail already has, so nothing is blank
        // when the ghost hands over; the sharper one swaps in after.
        this.big.removeAttribute('srcset');
        this.big.src = railImg.currentSrc || railImg.src;
        d.classList.add('is-ghosting');
      }
      d.hidden = false;
      if (typeof kkLock === 'function') kkLock();
      else document.body.classList.add('overflow-hidden');

      if (fly) {
        const to = this.restingRect(this.big);
        railImg.style.visibility = 'hidden';
        this.ghost(railImg.currentSrc || railImg.src, from, to, 460).then((ghost) => {
          d.classList.remove('is-ghosting');
          railImg.style.visibility = '';
          this.dropGhost(ghost);
          this.showColour(this.kk ? this.kk.color : null, false);
        });
      }
      requestAnimationFrame(() => d.classList.add('is-open'));
      setTimeout(() => {
        if (typeof trapFocus === 'function') trapFocus(this.panel, this.panel);
        else this.panel.focus();
      }, 60);
    }

    close(handOff = false) {
      if (!this.isOpen() || this.closing) return;
      this.closing = true;
      const d = this.dialog;
      const railImg = this.items[this.current].querySelector('.kk-rack__img--front');
      const big = this.big;
      const fly = !handOff && !reduced() && this.visible(railImg) && big.complete && big.naturalWidth > 0;
      const from = fly && big.getBoundingClientRect();
      d.classList.remove('is-open');
      d.classList.add('is-leaving');

      const finish = () => {
        d.hidden = true;
        d.classList.remove('is-leaving', 'is-ghosting');
        this.closing = false;
        if (typeof kkUnlock === 'function') kkUnlock();
        else document.body.classList.remove('overflow-hidden');
        // Back to the tee, unless the cart drawer is taking over after an add.
        const drawer = document.querySelector('cart-drawer');
        const drawerOpen = drawer && (drawer.holdsLock || drawer.classList.contains('active'));
        const tee = this.items[this.current].querySelector('[data-kk-tee]');
        if (typeof removeTrapFocus === 'function') removeTrapFocus(drawerOpen ? null : tee);
        else if (!drawerOpen) tee.focus({ preventScroll: true });
      };
      if (!fly) {
        setTimeout(finish, reduced() ? 0 : 200);
        return;
      }
      d.classList.add('is-ghosting');
      const to = railImg.getBoundingClientRect();
      railImg.style.visibility = 'hidden';
      this.ghost(big.currentSrc || big.src, from, to, 420).then((ghost) => {
        railImg.style.visibility = '';
        // The close-up may show another colour than the rail: fade the ghost off the
        // rail's own photo rather than swap.
        this.dropGhost(ghost);
        finish();
      });
    }

    // Where an element sits once the box has finished growing in (its CSS scale is
    // taken off for the measurement, without animating).
    restingRect(el) {
      const p = this.panel;
      p.style.transition = 'none';
      p.style.transform = 'none';
      const rect = el.getBoundingClientRect();
      p.style.transform = '';
      void p.offsetWidth;
      p.style.transition = '';
      return rect;
    }

    // A copy of the photo, fixed above everything, flown from one box to another
    // (transform only). Resolves with the copy, still in place, on landing.
    ghost(src, from, to, ms) {
      return new Promise((resolve) => {
        const g = document.createElement('img');
        g.className = 'kk-rack__ghost';
        g.alt = '';
        g.src = src;
        g.style.left = `${to.left}px`;
        g.style.top = `${to.top}px`;
        g.style.width = `${to.width}px`;
        g.style.height = `${to.height}px`;
        g.style.transform = `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width})`;
        document.body.appendChild(g);
        void g.offsetWidth;
        g.style.transition = `transform ${ms}ms cubic-bezier(0.2, 0.8, 0.2, 1)`;
        g.style.transform = 'none';
        let done = false;
        const land = () => {
          if (done) return;
          done = true;
          resolve(g);
        };
        g.addEventListener('transitionend', land, { once: true });
        setTimeout(land, ms + 150);
      });
    }

    dropGhost(g) {
      g.style.transition = 'opacity 160ms linear';
      g.style.opacity = '0';
      setTimeout(() => g.remove(), 200);
    }

    visible(el) {
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
    }

    disconnectedCallback() {
      // Theme editor re-renders: drop a close-up that was moved to <body>.
      if (this.dialog && this.dialog.parentElement === document.body && !this.isConnected) this.dialog.remove();
    }
  }

  customElements.define('kk-rack', KkRack);
})();
